import React, { useEffect, useState } from 'react'
import { authedAxios } from '../../utils'
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Box,
  Button,
  Tooltip,
} from '@mui/material'
import { Add, Check, Delete, Info } from '@mui/icons-material'

import { ALERTS_ACTIONS } from '../../store/alerts'
import { useStore } from '../../store'
import NewTodo from './new-todo'
import { removeItemAtIndex, removeItemFromArray } from '../../utils/array'
import PastTodayFuture from '../PastTodayFuture'
import useIsMobile from '../../hooks/useIsMobile'
import useAlert from '../../hooks/useAlert'

const COMPLETE_STATUS_ID = 3

const Todos = () => {
  const [todos, setTodos] = useState([])
  const [todoStatusTypes, setTodoStatusTypes] = useState([])
  const [addingTodo, setAddingTodo] = useState(false)

  const [isLoading, setIsLoading] = useState(false)
  const [hasError, setHasError] = useState(false)

  const { dispatch } = useStore()

  const today = new Date().toLocaleDateString('sv')
  const [startDate, setStartDate] = useState(today)
  const [endDate, setEndDate] = useState(today)

  const getTodos = () => {
    setIsLoading(true)
    authedAxios
      .get(`/api/todos?start=${startDate}&end=${endDate}`)
      .then((res) => {
        setTodos(res.data.todos)
      })
      .catch(() => {
        setHasError(true)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const getTodoStatusTypes = () => {
    authedAxios
      .get('/api/todos/types')
      .then((res) => {
        setTodoStatusTypes(res.data.types)
      })
      .catch(() => {})
  }

  useEffect(() => {
    getTodos()
  }, [startDate, endDate])

  useEffect(() => {
    getTodoStatusTypes()
  }, [])

  const { createGenericErrorAlert } = useAlert()

  const updateTodoStatusEventWrapper = (task, e) =>
    updateTodoStatus(task, e.target.value)

  const updateTodoStatus = (task, statusTypeId) => {
    setTodos(todos.map((t) => (t.id === task.id ? { ...t, statusTypeId } : t)))

    authedAxios
      .put('/api/todos', {
        ...task,
        statusTypeId,
      })
      .catch(() => {
        createGenericErrorAlert()
      })
  }

  const createTodo = (todo, cb) => {
    authedAxios
      .post('/api/todos', { ...todo, dueDate: todo.dueDate || null })
      .then((res) => {
        cb(null)
        setAddingTodo(false)
        setTodos([res.data.todo, ...todos])
      })
      .catch((e) => {
        dispatch({
          type: ALERTS_ACTIONS.CREATE_ALERT,
          message: 'Something went wrong creating your TODO item',
          severity: 'error',
        })

        cb(e)
      })
  }

  const deleteTodo = (todo) => {
    return authedAxios.delete(`/api/todos/${todo.id}`).then(() => {
      setTodos(removeItemFromArray(todos, todo))
    })
  }

  const isMobile = useIsMobile()

  const headerStyles = {
    position: 'sticky',
    top: 0,
    backgroundColor: 'white',
    zIndex: 1,
  }

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant='h6'>My Personal To-Do list</Typography>
          <Tooltip
            sx={{ ml: 2 }}
            title={
              <Typography sx={{ fontSize: '18px' }}>
                In this section, you can create a “Personalized” to-do list that
                is not tied to any account. Example: “Don’t forget to schedule
                your flight for next week"
              </Typography>
            }
          >
            <Info color='primary'></Info>
          </Tooltip>
        </Box>

        <PastTodayFuture
          startDate={startDate}
          endDate={endDate}
          setStartDate={setStartDate}
          setEndDate={setEndDate}
        />

        {isMobile && (
          <IconButton color='primary' onClick={() => setAddingTodo(true)}>
            <Add />
          </IconButton>
        )}
        {!isMobile && (
          <Button
            color='primary'
            onClick={() => setAddingTodo(true)}
            endIcon={<Add />}
            variant='outlined'
          >
            Add To-Do
          </Button>
        )}
      </Box>

      {addingTodo && (
        <NewTodo
          onCommit={createTodo}
          onCancel={() => setAddingTodo(false)}
        ></NewTodo>
      )}

      <TableContainer sx={{ height: 'calc(100% - 64px)' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={headerStyles}>Description</TableCell>
              <TableCell sx={headerStyles}>Status</TableCell>
              <TableCell sx={headerStyles}>Due Date</TableCell>
              <TableCell sx={headerStyles}></TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {todos.map((t) => {
              // Add timezone offset to the date so that it doesn't print as the day before
              const dueDate = t.dueDate
                ? new Date(
                    new Date(t.dueDate).getTime() +
                      new Date().getTimezoneOffset() * 60 * 1000
                  )
                : null

              const displayDate = dueDate
                ? dueDate.toLocaleDateString()
                : 'None'

              const isComplete = t.statusTypeId === COMPLETE_STATUS_ID

              return (
                <TableRow
                  key={t.id}
                  sx={{
                    textDecoration: isComplete ? 'line-through' : null,
                  }}
                >
                  <TableCell>{t.note}</TableCell>
                  <TableCell sx={{ width: '140px' }}>
                    <TextField
                      sx={{ width: '140px' }}
                      value={t.statusTypeId}
                      onChange={(e) => updateTodoStatusEventWrapper(t, e)}
                      select
                      size='small'
                    >
                      {todoStatusTypes.map((tst, i) => (
                        <MenuItem key={i} value={tst.id}>
                          {tst.name}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ width: '0.1%', whiteSpace: 'nowrap' }}>
                    {displayDate}
                  </TableCell>
                  <TableCell sx={{ width: '0.1%', whiteSpace: 'nowrap' }}>
                    {!isComplete && (
                      <>
                        <IconButton
                          color={'warning'}
                          onClick={() => deleteTodo(t)}
                        >
                          <Delete />
                        </IconButton>
                        <IconButton
                          color={'primary'}
                          onClick={() =>
                            updateTodoStatus(t, COMPLETE_STATUS_ID)
                          }
                        >
                          <Check />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
        {todos.length === 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Typography>
              <i>No To-Do's found</i>
            </Typography>
          </Box>
        )}
      </TableContainer>
    </>
  )
}

export default Todos
